import { idToken } from "./auth";

const BASE = import.meta.env.VITE_API_URL as string;

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await idToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      authorization: token,
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const json = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiError(res.status, json.error ?? "Request failed", json.detail);
  return json as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

/* --- Domain types --------------------------------------------------------- */

export type ItemStatus =
  | "draft" | "in_stock" | "listed" | "sold" | "shipped" | "donated" | "lost" | "damaged";
export type PhotoKind = "item" | "brand_tag" | "price_tag" | "flaw" | "listing";

/** Out of stock for good, no sale. Mirrors the API's WRITE_OFF_REASONS. */
export const WRITE_OFF_REASONS = [
  { reason: "damaged", label: "Damaged", hint: "Unsellable — stain, tear, broken" },
  { reason: "donated", label: "Donated", hint: "Given away rather than sold" },
  { reason: "lost", label: "Lost", hint: "Cannot be found" },
] as const;
export type WriteOffReason = (typeof WRITE_OFF_REASONS)[number]["reason"];

/** Condition, as the seller grades it. Mirrors the API's CONDITION_GRADES. */
export const CONDITION_GRADES = [
  { grade: "nwt", label: "NWT — new with tags" },
  { grade: "nwot", label: "NWOT — new without tags" },
  { grade: "excellent", label: "Excellent / like new" },
  { grade: "great", label: "Great" },
  { grade: "good", label: "Good" },
  { grade: "fair", label: "Fair" },
] as const;
export type ConditionGrade = (typeof CONDITION_GRADES)[number]["grade"];

/**
 * Where a find came from when it was not bought. Fixed source names the API
 * recognises (ORIGIN_SOURCES); picking one zeroes the purchase price. Still
 * business stock, unlike `isPersonal`.
 */
export const NO_COST_ORIGINS = [
  { name: "Own closet", label: "Our own closet", hint: "Came from the family's wardrobe — no purchase cost" },
  { name: "Gift", label: "Gift / free", hint: "Given to us — no purchase cost" },
] as const;
export const isNoCostOrigin = (store: string | null | undefined) =>
  !!store && NO_COST_ORIGINS.some((o) => o.name === store);

/** What the order was packed in. Mirrors the API's PACKAGING_TYPES. */
export const PACKAGING_TYPES = ["poly", "bubble", "other", "none"] as const;
export type Packaging = (typeof PACKAGING_TYPES)[number];

// Standardized photo orientations, mirrored from the API's PHOTO_SLOTS.
export const PHOTO_SLOTS = [
  { slot: "thrift_tag",        label: "Thrift Store Tag",              required: true },
  { slot: "brand_tag",         label: "Pad Print / Sewn Tag",          required: true },
  { slot: "flat_front",        label: "Garment — Flat — Front",        required: true },
  { slot: "flat_back",         label: "Garment — Flat — Back",         required: true },
  { slot: "mannequin_front",   label: "Garment — Mannequin — Front",       required: false },
  { slot: "mannequin_left",    label: "Garment — Mannequin — Left Side",   required: false },
  { slot: "mannequin_back",    label: "Garment — Mannequin — Back",        required: false },
  { slot: "mannequin_right",   label: "Garment — Mannequin — Right Side",  required: false },
  { slot: "measure_vertical",  label: "Garment — Vertical Measurement",    required: false },
  { slot: "measure_horizontal",label: "Garment — Horizontal Measurement",  required: false },
] as const;

export const ADDITIONAL_SLOT = "additional";
export type PhotoSlot = (typeof PHOTO_SLOTS)[number]["slot"] | typeof ADDITIONAL_SLOT;

// The primary/thumbnail photo is the flat front of the garment.
const PRIMARY_SLOT = "flat_front";

export interface ItemSummary {
  id: string;
  sku: string;
  title: string | null;
  brand: string | null;
  category: string | null;
  season: string | null;
  audience: string | null;
  sizeLabel: string | null;
  status: ItemStatus;
  /** Bought for yourself, not for resale — excluded from business figures. */
  isPersonal: boolean;
  enrichmentStatus: string;
  purchasePrice: string | null;
  tagPrice: string | null;
  suggestedList: string | null;
  suggestedLow: string | null;
  suggestedHigh: string | null;
  activeListings: number;
  thumbUrl: string | null; // presigned flat-front (or best available) photo
  store?: string;
}

export interface MarketProfile {
  suggestedLow: string | null;
  suggestedHigh: string | null;
  suggestedList: string | null;
  floorPrice: string | null;
  retailReference: string | null;
  compCount: number;
  soldCompCount: number;
  medianSold: string | null;
  sellThroughRate: string | null;
  medianDaysToSale: number | null;
  bestMonths: number[] | null;
  confidence: string;
  rationale: string | null;
  computedAt: string;
}

export interface ItemDetail extends ItemSummary {
  subcategory: string | null;
  /** Still stored (and written by vision); no longer edited. Older items carry typed weights here. */
  sizeSystem: string | null;
  colorway: string | null;
  conditionGrade: string | null;
  conditionNotes: string | null;
  purchaseDate: string | null;
  storageBin: string | null;
  archivedAt: string | null;
  enrichmentError: string | null;
  /** Vision output and provenance. `manualFields` lists what the seller set by hand. */
  attributes: Record<string, unknown> & { manualFields?: string[]; classifiedAt?: string; itemWeight?: number; itemWeightUnit?: "oz" | "lbs" | "kg";
    packagingType?: "polybag" | "box"; packageDims?: { length: number | null; width: number | null; height: number | null; unit: "in" } };
  /** Garment measurements: { garment: "top" | "bottom", unit: "in", pit_to_pit: 21, … }. */
  measurements: Record<string, unknown> | null;
  photos: Array<{ id: string; kind: PhotoKind; slot: string | null; s3Key: string; isPrimary: boolean }>;
  listings: Array<{
    id: string; platform: string; status: string; listPrice: string | null;
    externalUrl: string | null; syncError: string | null;
  }>;
  sales: Array<{ id: string; platform: string; salePrice: string; soldAt: string; returned?: boolean }>;
  costs: Array<{ id: string; kind: string; amount: string; isEstimate: boolean }>;
  marketProfile: MarketProfile | null;
  comps: Array<{ source: string; title: string; url: string; price: string; soldAt: string | null }>;
  ledger: { netProfit: string | null; daysToSale: number | null; daysHeld: number | null } | null;
}

/* --- Photo upload --------------------------------------------------------- */

export interface UploadResult { s3Key: string; photoId: string }

/**
 * Two steps on purpose: the browser PUTs the bytes straight to S3, then tells
 * the API the object exists. Large iPhone photos never touch the API payload.
 */
export async function uploadPhoto(
  itemId: string,
  slot: PhotoSlot,
  file: File,
  onProgress?: (fraction: number) => void
): Promise<UploadResult> {
  const contentType = file.type || "image/jpeg";
  const { uploadUrl, s3Key } = await api.post<{ uploadUrl: string; s3Key: string }>(
    "/photos/presign",
    { itemId, slot, contentType }
  );

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("content-type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed with status ${xhr.status}`));
    xhr.onerror = () => reject(new Error("Upload failed. Check your connection."));
    xhr.send(file);
  });

  const { id } = await api.post<{ id: string }>(`/items/${itemId}/photos`, {
    slot,
    s3Key,
    contentType,
    isPrimary: slot === PRIMARY_SLOT,
  });

  return { s3Key, photoId: id };
}
